import React, { useMemo, useState, useCallback, useContext } from 'react';
import styled from 'styled-components';
import useFilters from '@ubeya/shared/hooks/account/useFilters';
import useFields, { FieldTypes } from '@ubeya/shared/hooks/account/useFields';
import { FILTERS_IDS } from '@ubeya/shared/constants';
import DropDown from '../DropDown';
import { Small } from '../Typography';
import { FlexCenter, flexColumnCenter } from '../Flex';
import BaseSearch from '../Search';
import { counterFormatter, formatOptions, applyFilter, applyFilters } from '../../utils/filters';
import ConfigContext from '../../contexts/ConfigContext';
import {
  CHAT_FILTERS_SLUGS,
  GENERAL_FILTERS_SLUGS,
  PROJECT_POST_FILTERS_SLUGS,
  PROJECT_SCHEDULING_FILTERS_SLUGS
} from '../../constants';
import { Loader } from '../Loader';
import TimeFilter from './CustomFilters/TimeFilter';
import PositionsFilter from './CustomFilters/PositionsFilter';
import AgeFilter from './CustomFilters/AgeFilter';
import RatingFilter from './CustomFilters/RatingFilter';
import FieldsFilter from './CustomFilters/FieldsFilter';
import EmployeeTypeFilter from './CustomFilters/EmployeeTypeFilter';
import AdminTypeFilter from './CustomFilters/AdminTypeFilter';
import AdminFilter from './CustomFilters/AdminFilter';
import TagsFilter from './CustomFilters/TagsFilter';
import ClientsFilter from './CustomFilters/ClientsFilter';
import PreferredClientsFilter from './CustomFilters/PreferredClientsFilter';
import BranchesFilter from './CustomFilters/BranchesFilter';
import ApprovalFilter from './CustomFilters/ApprovalFilter';
import ClientApprovalFilter from './CustomFilters/ClientApprovalFilter';
import SuppliersFilter from './CustomFilters/SuppliersFilter';
import WorkedWithClientFilter from './CustomFilters/WorkedWithClientFilter';
import ProcessStatusFilter from './CustomFilters/ProcessStatusFilter';
import LocationsFilter from './CustomFilters/LocationsFilter';
import DistanceFromLocationFilter from './CustomFilters/DistanceFromLocationFilter';
import HapAppFilter from './CustomFilters/HasAppFilter';
import SearchableOptions from './SearchableOptions';
import DateFilter from './CustomFilters/DateFilter';
import BookingStatusFilter from './CustomFilters/BookingStatusFilter';
import ProjectShiftStatusFilter from './CustomFilters/ProjectShiftStatusFilter';
import OrderStatusFilter from './CustomFilters/OrderStatusFilter';
import ProjectLiveViewPositionsFilter from './CustomFilters/ProjectLiveViewPositionsFilter';
import ProjectLiveViewLocationsFilter from './CustomFilters/ProjectLiveViewLocationsFilter';
import ProjectLiveViewDateFilter from './CustomFilters/ProjectLiveViewDateFilter';
import ProjectLiveViewStartTimeFilter from './CustomFilters/ProjectLiveViewStartTimeFilter';
import EmploymentTypeFilter from './CustomFilters/EmploymentTypeFilter';
import WorkedInAreaFilter from './CustomFilters/WorkedInAreaFilter';
import DepartmentFilter from './CustomFilters/DepartmentFilter';
import ProjectLiveViewEndTimeFilter from './CustomFilters/ProjectLiveViewEndTimeFilter';
import ProjectLiveViewSuppliersFilter from './CustomFilters/ProjectLiveViewSuppliersFilter';
import ProjectLiveViewBookingStatusFilter from './CustomFilters/ProjectLiveViewBookingStatusFilter';
import ProjectLiveViewBranchCostCentersFilter from './CustomFilters/ProjectLiveViewBranchCostCentersFilter';
import ProjectAgeWarningFilter from './CustomFilters/ProjectAgeWarningFilter';
import ChatTagsFilter from './CustomFilters/ChatTagsFilter';
import ChatBranchesFilter from './CustomFilters/ChatBranchesFilter';
import ChatStatusesFilter from './CustomFilters/ChatStatusesFilter';
import CostCenterFilter from './CustomFilters/CostCenterFilter';
import HasTimeFilter from './CustomFilters/HasTimeFilter';

const LoaderWrapper = styled(FlexCenter)`
  flex: 1;
`;

const EmptyComponentRenderer = () => <></>;

const COMPONENTS_MAP = {
  positions: PositionsFilter,
  startTimes: TimeFilter,
  endTimes: TimeFilter,

  [GENERAL_FILTERS_SLUGS.WITH_CLOCK_IN]: (params) =>
    HasTimeFilter({ ...params, title: GENERAL_FILTERS_SLUGS.WITH_CLOCK_IN }),
  [GENERAL_FILTERS_SLUGS.WITH_CLOCK_OUT]: (params) =>
    HasTimeFilter({ ...params, title: GENERAL_FILTERS_SLUGS.WITH_CLOCK_OUT }),
  [GENERAL_FILTERS_SLUGS.WITH_CHECK_IN]: (params) =>
    HasTimeFilter({ ...params, title: GENERAL_FILTERS_SLUGS.WITH_CHECK_IN }),
  [GENERAL_FILTERS_SLUGS.WITH_CHECK_OUT]: (params) =>
    HasTimeFilter({ ...params, title: GENERAL_FILTERS_SLUGS.WITH_CHECK_OUT }),
  [GENERAL_FILTERS_SLUGS.WITH_BREAKS]: (params) =>
    HasTimeFilter({ ...params, title: GENERAL_FILTERS_SLUGS.WITH_BREAKS }),

  age: AgeFilter,
  rating: RatingFilter,
  employeeType: EmployeeTypeFilter,
  adminType: AdminTypeFilter,
  eventBooker: AdminFilter,
  restrictedClient: ClientsFilter,
  tags: TagsFilter,
  clients: ClientsFilter,
  costCenter: CostCenterFilter,
  payrollProcessStatus: ProcessStatusFilter,
  suppliers: SuppliersFilter,
  approved: ApprovalFilter,
  clientApproval: ClientApprovalFilter,
  locations: LocationsFilter,
  hasApp: HapAppFilter,
  department: DepartmentFilter,
  employmentType: EmploymentTypeFilter,
  workedInArea: WorkedInAreaFilter,
  workedWithClient: WorkedWithClientFilter,
  notBookedOnDate: DateFilter,
  branches: BranchesFilter,
  preferredClients: PreferredClientsFilter,
  startEmploymentDate: DateFilter,
  bookingStatus: BookingStatusFilter,
  availableByTimeAvailability: EmptyComponentRenderer,
  orderStatusFilter: OrderStatusFilter,

  [PROJECT_POST_FILTERS_SLUGS.positions]: ProjectLiveViewPositionsFilter,
  [PROJECT_POST_FILTERS_SLUGS.locations]: ProjectLiveViewLocationsFilter,
  [PROJECT_POST_FILTERS_SLUGS.dates]: ProjectLiveViewDateFilter,

  [PROJECT_SCHEDULING_FILTERS_SLUGS.positions]: ProjectLiveViewPositionsFilter,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.locations]: ProjectLiveViewLocationsFilter,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.startTime]: ProjectLiveViewStartTimeFilter,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.endTime]: ProjectLiveViewEndTimeFilter,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.bookingStatus]: ProjectLiveViewBookingStatusFilter,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.bookingIssues]: ProjectShiftStatusFilter,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.suppliers]: ProjectLiveViewSuppliersFilter,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.branchCostCenters]: ProjectLiveViewBranchCostCentersFilter,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.ageWarning]: ProjectAgeWarningFilter,

  [CHAT_FILTERS_SLUGS.tags]: ChatTagsFilter,
  [CHAT_FILTERS_SLUGS.branches]: ChatBranchesFilter,
  [CHAT_FILTERS_SLUGS.statuses]: ChatStatusesFilter
};

const Search = styled(BaseSearch)`
  min-height: 40px;
`;
const InnerSearch = styled(BaseSearch)`
  min-height: 40px;
  min-width: 210px;
`;

const Counter = styled(Small)`
  ${flexColumnCenter};
  width: 17px;
  height: 14px;
  border-radius: 7px;
  background-color: ${({ theme }) => theme.colors.disabled};
  color: ${({ theme }) => theme.colors.white};
  margin-left: 7px;
`;

const Clear = styled(Small)`
  color: ${({ theme }) => theme.colors.primary};
  white-space: nowrap;
  cursor: pointer;
  margin-left: 9px;
  margin-top: 10px;
`;

const FilterOptions = ({
  selectedKey,
  showSearchBox,
  search,
  setSearch,
  filters,
  setFilters,
  selectedOpenFilter,
  filterContextParams,
  disabledFilters,
  externalOptions,
  doApiCall = true
}) => {
  const { t } = useContext(ConfigContext);
  const { filters: filtersData, isLoading } = useFilters({ enabled: doApiCall });
  const { mappedFields } = useFields({ enabled: doApiCall });
  const [openFilter, setOpenFilter] = useState(selectedOpenFilter);
  const [innerSpecificFilterSearch, setInnerSpecificFilterSearch] = useState('');

  const basicOptions = useMemo(
    () =>
      formatOptions({ options: filtersData[selectedKey], type: 'basic', filters }).filter(({ slug: filterSlug }) => {
        if (externalOptions?.[filterSlug]?.length === 0) {
          return false;
        }
        return ![
          'bookedInProjects',
          'restrictedClient',
          'isShiftType',
          'notBookedOnDate',
          'availableByTimeAvailability'
        ].includes(filterSlug);
      }),
    [externalOptions, filtersData, filters, selectedKey]
  );

  const fieldsOptions = useMemo(
    () =>
      formatOptions({
        options: filtersData[selectedKey],
        type: 'fields',
        formatSlug: (id) => mappedFields?.[id]?.name,
        filters
      }),
    [filtersData, filters, mappedFields, selectedKey]
  );

  const getCounter = useCallback(
    (filter) => (counterFormatter(filter.value) ? <Counter>{counterFormatter(filter.value)}</Counter> : null),
    []
  );

  const handleApply = useCallback(
    (value) => {
      const { type, id, slug } = openFilter;

      setFilters(applyFilter({ type, id, value, slug, allFilters: filters }));
      !selectedOpenFilter && setOpenFilter();
    },
    [filters, openFilter, selectedOpenFilter, setFilters]
  );

  const handleApplyAll = useCallback(
    (values) => {
      setFilters(applyFilters({ values, allFilters: filters }));
      setSearch('');
    },
    [setFilters, filters, setSearch]
  );

  const mappedBasicOptions = useMemo(
    () =>
      (filtersData[selectedKey]?.basic || []).reduce((all, curr) => {
        all[curr.id] = curr;
        return all;
      }, {}),
    [filtersData, selectedKey]
  );

  const handleResetFilters = useCallback(() => {
    const postClearFilters = {
      basic: (filters?.basic || []).filter(({ id }) =>
        mappedBasicOptions?.[id]?.slug ? (disabledFilters || []).includes(mappedBasicOptions[id].slug) : false
      )
    };

    setFilters(postClearFilters);
  }, [disabledFilters, filters?.basic, mappedBasicOptions, setFilters]);

  const hasFilters = useMemo(() => filters?.basic?.length > 0 || filters?.fields?.length > 0, [
    filters?.basic?.length,
    filters?.fields?.length
  ]);

  if (openFilter) {
    const Component =
      COMPONENTS_MAP[openFilter.slug] ||
      (FieldTypes &&
      !!mappedFields[openFilter.id]?.fieldTypeId &&
      mappedFields[openFilter.id].fieldTypeId === FieldTypes.FIELD_TYPE_ADDRESS
        ? DistanceFromLocationFilter
        : FieldsFilter);

    return (
      <>
        {showSearchBox && (
          <InnerSearch value={innerSpecificFilterSearch} onChange={setInnerSpecificFilterSearch} keepOpen focus />
        )}
        <Component
          id={openFilter.id}
          slug={openFilter.slug}
          search={innerSpecificFilterSearch}
          defaultValue={openFilter.value}
          handleClose={selectedOpenFilter ? undefined : () => setOpenFilter()}
          onApply={handleApply}
          filterContextParams={filterContextParams}
          isDisabled={openFilter.id !== FILTERS_IDS.START_EMPLOYMENT_DATE}
          basicFilters={filters?.basic || []}
          externalOptions={externalOptions}
          hideLayout={!!search}
        />
      </>
    );
  }

  if (isLoading) {
    return (
      <LoaderWrapper>
        <Loader size="small" />
      </LoaderWrapper>
    );
  }

  return (
    <>
      {showSearchBox && <Search value={search} onChange={setSearch} keepOpen />}
      {hasFilters && <Clear onClick={handleResetFilters}>{t('clear')} (x)</Clear>}
      {search ? (
        <SearchableOptions
          search={search}
          basicOptions={basicOptions}
          fieldsOptions={fieldsOptions}
          COMPONENTS_MAP={COMPONENTS_MAP}
          onApply={handleApplyAll}
          externalOptions={externalOptions}
        />
      ) : (
        <>
          {basicOptions.map((filter, index) => {
            return (
              <DropDown.Item
                key={`basic-${index}`}
                useTextWithTooltip
                onClick={() => setOpenFilter(filter)}
                closeOnClick={false}
                isDisabled={disabledFilters.includes(filter.slug)}>
                {t(filter.slug)} {getCounter(filter)}
              </DropDown.Item>
            );
          })}

          {basicOptions.length > 0 && fieldsOptions.length > 0 && <DropDown.Separator />}

          {fieldsOptions.map((filter, index) => (
            <DropDown.Item
              key={`field-${index}`}
              useTextWithTooltip
              onClick={() => setOpenFilter(filter)}
              closeOnClick={false}
              isDisabled={disabledFilters.includes(filter.slug)}>
              {t(filter.slug)} {getCounter(filter)}
            </DropDown.Item>
          ))}
        </>
      )}
    </>
  );
};

export default FilterOptions;
